import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ProtocolCard from "@/components/ProtocolCard";
import { Target, Trash2, Sparkles, Check, Clock } from "lucide-react";

function ProgressBar({ pct }) {
  const p = pct == null ? 0 : pct;
  return (
    <div className="h-2.5 rounded-full bg-[#E2D8C8] overflow-hidden" data-testid="twin-progress-bar">
      <div className="h-full rounded-full bg-gradient-to-r from-[#3F6B4C] to-[#D49A44] transition-all" style={{ width: `${p}%` }} />
    </div>
  );
}

function TwinItem({ twin, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [protocol, setProtocol] = useState(twin.protocol || null);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bioage/twins/${twin.id}/protocol`);
      setProtocol(data);
      toast.success("Massey drafted your protocol.");
    } catch (e) {
      const msg = e?.response?.status === 402
        ? "A Vital or Sovereign membership is needed for Massey protocols."
        : "Could not draft this protocol right now.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/bioage/twins/${twin.id}`);
      toast.success("Twin removed.");
      onChanged && onChanged();
    } catch {
      toast.error("Could not remove this twin.");
      setBusy(false);
    }
  };

  const target = twin.target_adjusted_bioage;
  const current = twin.current_adjusted_bioage;
  const reached = twin.progress_pct != null && twin.progress_pct >= 100;

  return (
    <div className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-2xl p-6" data-testid={`saved-twin-${twin.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#C05C42]" />
            <p className="font-serif text-lg text-[#2D322B] leading-tight" data-testid="saved-twin-label">{twin.label}</p>
          </div>
          <p className="text-xs text-[#5C6359] mt-1">
            Target bio-age <strong className="text-[#3F6B4C]">{target ?? "—"}</strong>
            {current != null && <> · you&apos;re at <strong className="text-[#2D322B]">{current}</strong></>}
          </p>
        </div>
        <button onClick={remove} disabled={busy} data-testid={`saved-twin-delete-${twin.id}`} className="text-[#5C6359] hover:text-[#C05C42] shrink-0" aria-label="Remove twin">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {twin.changes?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="saved-twin-changes">
          {twin.changes.map((c, i) => (
            <span key={i} className="text-xs px-3 py-1 rounded-full bg-[#F1ECE4] border border-[#D1C7B8] text-[#2D322B]">{c}</span>
          ))}
        </div>
      )}

      {twin.progress_pct != null && (
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-[#5C6359] mb-1.5">
            <span className="uppercase tracking-widest">Progress</span>
            <span className="font-semibold text-[#2D322B]" data-testid="saved-twin-progress">{twin.progress_pct}%</span>
          </div>
          <ProgressBar pct={twin.progress_pct} />
          <p className="text-xs text-[#5C6359] mt-2 flex items-center gap-1.5">
            {reached ? <><Check className="w-3.5 h-3.5 text-[#3F6B4C]" /> Target reached — beautiful work.</>
              : <><Clock className="w-3.5 h-3.5 text-[#D49A44]" /> {twin.years_to_go} yrs to your target</>}
          </p>
        </div>
      )}

      {(twin.habits_met?.length > 0 || twin.habits_pending?.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-4 text-xs">
          {twin.habits_met?.length > 0 && (
            <div data-testid="saved-twin-habits-met">
              <span className="text-[#3F6B4C] font-medium">Done:</span> <span className="text-[#5C6359]">{twin.habits_met.join(", ")}</span>
            </div>
          )}
          {twin.habits_pending?.length > 0 && (
            <div data-testid="saved-twin-habits-pending">
              <span className="text-[#C05C42] font-medium">Next:</span> <span className="text-[#5C6359]">{twin.habits_pending.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-5">
        {protocol ? (
          <ProtocolCard protocol={protocol} testid={`saved-twin-protocol-${twin.id}`} />
        ) : (
          <button onClick={generate} disabled={busy} data-testid={`saved-twin-generate-${twin.id}`} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60">
            <Sparkles className="w-4 h-4 text-[#C05C42]" /> {busy ? "Massey is drafting…" : "Turn into a protocol with Massey"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SavedTwins({ twins, onChanged }) {
  if (!twins || twins.length === 0) return null;
  return (
    <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8 sm:p-10" data-testid="saved-twins">
      <div className="flex items-center gap-3 mb-2">
        <Target className="w-5 h-5 text-[#C05C42]" />
        <p className="label-eyebrow">Your saved twins</p>
      </div>
      <p className="font-serif text-2xl text-[#2D322B] leading-snug max-w-2xl mb-6">
        Target scenarios you&apos;re working toward. Watch the bar fill as your habits and labs move.
      </p>
      <div className="space-y-5">
        {twins.map((t) => <TwinItem key={t.id} twin={t} onChanged={onChanged} />)}
      </div>
    </div>
  );
}
